// Tutorial prompts as data (decision 0060: contextual prompts teaching each verb the first time it's needed,
// device-aware, each retiring once its verb is used). Each prompt is tied to a verb (an action id); the game draws it
// with the glyph of the device used last. A prompt is due when the prompts it comes `after` have retired and its
// condition (`when`, the game's fact language) has held for `delay` seconds; one shows at a time, the first due in the
// table's order. It hides (not retired) when its condition stops holding. `use(verb)` retires every prompt for that
// verb, even one never shown (the player found it alone), unless the prompt's `usedWhen` doesn't hold (glide shares
// jump's button: a press counts as gliding only in the air). Retired prompts are a set: JSON, merged by union.
// The hints setting is the game's: `update(dt, false)` shows nothing and keeps retiring.
import { META_FAULTS } from "./faults";
import type { FactTest } from "./rules";

/** One prompt. */
export interface TutorialDef {
  id: string;
  /** The action it teaches. */
  verb: string;
  /** When it is needed (a condition); default always. */
  when?: string;
  /** Prompts that must retire first. */
  after?: readonly string[];
  /** Seconds `when` must hold before it shows (default 0). */
  delay?: number;
  /** A use of the verb retires it only while this holds (default always). */
  usedWhen?: string;
}

/** The saved state: each retired prompt's place in the order retired (from 1). */
export interface TutorialState {
  retired: Record<string, number>;
}

/** What `update` reports. */
export interface TutorialShown {
  id: string;
  verb: string;
  /** Seconds it has been showing. */
  shown: number;
}

/** A fresh state. */
export const emptyTutorials = (): TutorialState => ({ retired: {} });

/** Merge two states: a prompt retired on either side is retired. */
export function mergeTutorials(a: TutorialState, b: TutorialState): TutorialState {
  const out = structuredClone(a);
  const next = Object.values(out.retired).reduce((m, v) => Math.max(m, v), 0);
  Object.entries(b.retired)
    .filter(([id]) => !(id in out.retired))
    .sort((x, y) => x[1] - y[1])
    .forEach(([id], i) => (out.retired[id] = next + i + 1));
  return out;
}

/** Problems with the prompts (empty when sound): duplicates, an `after` naming no prompt, a cycle-free order. */
export function checkTutorials(defs: readonly TutorialDef[], verbs?: readonly string[]): string[] {
  const out: string[] = [];
  const ids = new Set<string>();
  for (const d of defs) {
    if (ids.has(d.id)) out.push(`tutorial "${d.id}" is defined twice`);
    ids.add(d.id);
    if (verbs && !verbs.includes(d.verb)) out.push(`tutorial "${d.id}" teaches "${d.verb}", which is not an action`);
    if (d.delay !== undefined && !(d.delay >= 0)) out.push(`tutorial "${d.id}": delay must be >= 0`);
  }
  for (const d of defs) for (const a of d.after ?? []) if (!ids.has(a)) out.push(`tutorial "${d.id}" comes after "${a}", which is no prompt`);
  return out;
}

/** The prompts. */
export class Tutorials {
  private s: TutorialState = emptyTutorials();
  /** Seconds each prompt's condition has held. */
  private held = new Map<string, number>();
  private showing: { id: string; shown: number } | null = null;

  /** `test` evaluates `when` and `usedWhen` (without it, prompts with conditions never show or retire). */
  constructor(readonly defs: readonly TutorialDef[], private readonly o: { test?: FactTest } = {}) {
    const bad = checkTutorials(defs);
    if (bad.length) throw new Error(`tutorials: ${bad.join("; ")}`);
  }

  /** Step by `dt` seconds; returns the prompt to show now (null: none, or hints off). */
  update(dt: number, enabled = true): TutorialShown | null {
    let pick: TutorialDef | null = null;
    for (const d of this.defs) {
      if (this.retired(d.id)) {
        this.held.delete(d.id);
        continue;
      }
      const ready = (d.after ?? []).every((a) => this.retired(a)) && this.holds(d.when);
      const h = ready ? (this.held.get(d.id) ?? 0) + Math.max(0, dt) : 0;
      this.held.set(d.id, h);
      if (!pick && ready && h >= (d.delay ?? 0) - 1e-9) pick = d;
    }
    if (!enabled || !pick) {
      this.showing = null;
      return null;
    }
    this.showing = this.showing?.id === pick.id ? { id: pick.id, shown: this.showing.shown + Math.max(0, dt) } : { id: pick.id, shown: 0 };
    return { id: pick.id, verb: pick.verb, shown: this.showing.shown };
  }

  /** The prompt showing since the last `update`, or null. */
  current(): string | null {
    return this.showing?.id ?? null;
  }

  /** The verb was used: retire its prompts (those whose `usedWhen` holds). Returns the ids retired. */
  use(verb: string): string[] {
    if (META_FAULTS.tutorialNeverRetires) return [];
    const out: string[] = [];
    for (const d of this.defs) {
      if (d.verb !== verb || this.retired(d.id) || !this.holds(d.usedWhen)) continue;
      this.s.retired[d.id] = Object.keys(this.s.retired).length + 1;
      out.push(d.id);
    }
    if (this.showing && out.includes(this.showing.id)) this.showing = null;
    return out;
  }

  /** Whether a prompt has retired. */
  retired(id: string): boolean {
    return id in this.s.retired;
  }

  /** The verbs the prompts teach (what the game should report uses of). */
  verbs(): string[] {
    return [...new Set(this.defs.map((d) => d.verb))];
  }

  /** Every prompt's retirement, in the table's order. */
  list(): { id: string; verb: string; retired: boolean }[] {
    return this.defs.map((d) => ({ id: d.id, verb: d.verb, retired: this.retired(d.id) }));
  }

  snapshot(): TutorialState {
    return structuredClone(this.s);
  }

  restore(s: TutorialState): void {
    this.s = structuredClone({ ...emptyTutorials(), ...s });
    this.held.clear();
    this.showing = null;
  }

  /** Merge another state in (a sync). */
  merge(other: TutorialState): void {
    this.s = mergeTutorials(this.s, other);
  }

  /** Every prompt back to unretired (a "show the tips again" button). */
  reset(): void {
    this.restore(emptyTutorials());
  }

  private holds(cond: string | undefined): boolean {
    if (!cond) return true;
    if (!this.o.test) return false;
    try {
      return this.o.test(cond);
    } catch {
      return false;
    }
  }
}
